import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSectionScrollProgress } from "@/hooks/useSharedScroll";

/**
 * Invitation — final chapter (05) of the home narrative.
 *
 * Three-column symmetric ceremony layout:
 *   [ right-aligned text ]  [ centered portrait image ]  [ left-aligned text ]
 *
 * SCROLL-DRIVEN motion with clear ORDER (text first, then image rotates in):
 *   · Outer section is 200vh — provides a scroll runway.
 *   · Inner stage is `sticky top-0 h-screen` — pinned exactly one screen.
 *   · Progress p ∈ [0, 1] is derived from how much of the runway has been scrolled.
 *
 *   Phase A — TEXT (p ∈ [0.00, 0.35]):
 *       Left phrase slides in from the left, right phrase slides in from the right.
 *       Image stays fully hidden (opacity 0, rotated -720°, scale 0.5).
 *
 *   Phase B — IMAGE (p ∈ [0.35, 0.95]):
 *       Image rotates from -720° → 0° (two full spins),
 *       scales from 0.5 → 1, and fades from 0 → 1.
 *
 *   Phase C — META (p ∈ [0.60, 1.00]):
 *       Top label, corner meta, CTA buttons, footnote fade in together as final polish.
 *
 * Implementation:
 *   Direct DOM style writes inside a single rAF loop — zero React state, zero re-renders.
 */

export default function Invitation() {
  const sectionRef = useRef<HTMLElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const leftTextRef = useRef<HTMLDivElement>(null);
  const rightTextRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const footnoteRef = useRef<HTMLDivElement>(null);
  const bottomLeftRef = useRef<HTMLDivElement>(null);
  const bottomRightRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  // Diff-cache — mirrors the pattern used in every other home section.
  // Without this, every scroll frame writes opacity + transform to all
  // 8 DOM elements even when the value hasn't meaningfully changed. This
  // was Invitation's only remaining perf sin and the last source of
  // "end-of-page" scroll stutter after the shared-broadcaster pass.
  const lastPaintedRef = useRef({
    leftOp: -1,
    leftX: -9999,
    rightOp: -1,
    rightX: -9999,
    imgOp: -1,
    imgRot: -9999,
    imgScale: -1,
    labelOp: -1,
    blOp: -1,
    brOp: -1,
    btnOp: -1,
    btnY: -9999,
    footOp: -1,
    footY: -9999,
  });

  // Mark will-change on mount so the browser prepares GPU layers.
  useEffect(() => {
    const els = [
      imageRef.current,
      leftTextRef.current,
      rightTextRef.current,
      labelRef.current,
      buttonsRef.current,
      footnoteRef.current,
      bottomLeftRef.current,
      bottomRightRef.current,
    ];
    els.forEach((el) => {
      if (el) el.style.willChange = "opacity, transform";
    });
  }, []);

  useSectionScrollProgress(sectionRef, (p, inView) => {
    if (!inView) return;
    const clamp = (v: number, min: number, max: number) =>
      Math.max(min, Math.min(max, v));
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const last = lastPaintedRef.current;

    // Compute derived values first (pure math, no DOM)
    const tText = clamp(p / 0.35, 0, 1);
    const eText = easeOutCubic(tText);
    const leftOp = eText;
    const leftX = -80 * (1 - eText);
    const rightOp = eText;
    const rightX = 80 * (1 - eText);

    const tImg = clamp((p - 0.35) / (0.95 - 0.35), 0, 1);
    const eImg = easeOutCubic(tImg);
    const imgOp = eImg;
    const imgRot = -720 * (1 - eImg);
    const imgScale = 0.5 + 0.5 * eImg;

    const tMeta = clamp((p - 0.6) / 0.4, 0, 1);
    const eMeta = easeOutCubic(tMeta);
    const labelOp = eMeta;
    const blOp = eMeta * 0.9;
    const brOp = eMeta * 0.9;
    const btnOp = eMeta;
    const btnY = 20 * (1 - eMeta);
    const footOp = eMeta;
    const footY = 12 * (1 - eMeta);

    // Left text — only write when values actually shift
    const leftText = leftTextRef.current;
    if (leftText) {
      if (Math.abs(leftOp - last.leftOp) > 0.005) {
        leftText.style.opacity = leftOp.toFixed(3);
        last.leftOp = leftOp;
      }
      if (Math.abs(leftX - last.leftX) > 0.05) {
        leftText.style.transform = `translate3d(${leftX.toFixed(2)}px, 0, 0)`;
        last.leftX = leftX;
      }
    }

    // Right text
    const rightText = rightTextRef.current;
    if (rightText) {
      if (Math.abs(rightOp - last.rightOp) > 0.005) {
        rightText.style.opacity = rightOp.toFixed(3);
        last.rightOp = rightOp;
      }
      if (Math.abs(rightX - last.rightX) > 0.05) {
        rightText.style.transform = `translate3d(${rightX.toFixed(2)}px, 0, 0)`;
        last.rightX = rightX;
      }
    }

    // Image — rotate+scale change together, gate on either shifting
    const image = imageRef.current;
    if (image) {
      if (Math.abs(imgOp - last.imgOp) > 0.005) {
        image.style.opacity = imgOp.toFixed(3);
        last.imgOp = imgOp;
      }
      const rotChanged = Math.abs(imgRot - last.imgRot) > 0.05;
      const scaleChanged = Math.abs(imgScale - last.imgScale) > 0.002;
      if (rotChanged || scaleChanged) {
        image.style.transform = `rotate(${imgRot.toFixed(2)}deg) scale(${imgScale.toFixed(3)})`;
        last.imgRot = imgRot;
        last.imgScale = imgScale;
      }
    }

    // Meta cluster
    const label = labelRef.current;
    if (label && Math.abs(labelOp - last.labelOp) > 0.005) {
      label.style.opacity = labelOp.toFixed(3);
      last.labelOp = labelOp;
    }
    const bottomLeft = bottomLeftRef.current;
    if (bottomLeft && Math.abs(blOp - last.blOp) > 0.005) {
      bottomLeft.style.opacity = blOp.toFixed(3);
      last.blOp = blOp;
    }
    const bottomRight = bottomRightRef.current;
    if (bottomRight && Math.abs(brOp - last.brOp) > 0.005) {
      bottomRight.style.opacity = brOp.toFixed(3);
      last.brOp = brOp;
    }

    const buttons = buttonsRef.current;
    if (buttons) {
      if (Math.abs(btnOp - last.btnOp) > 0.005) {
        buttons.style.opacity = btnOp.toFixed(3);
        last.btnOp = btnOp;
      }
      if (Math.abs(btnY - last.btnY) > 0.05) {
        buttons.style.transform = `translate3d(0, ${btnY.toFixed(2)}px, 0)`;
        last.btnY = btnY;
      }
    }

    const footnote = footnoteRef.current;
    if (footnote) {
      if (Math.abs(footOp - last.footOp) > 0.005) {
        footnote.style.opacity = footOp.toFixed(3);
        last.footOp = footOp;
      }
      if (Math.abs(footY - last.footY) > 0.05) {
        footnote.style.transform = `translate3d(0, ${footY.toFixed(2)}px, 0)`;
        last.footY = footY;
      }
    }
  });

  // Press feedback then navigate.
  const pressAndGo = (
    e: React.MouseEvent<HTMLButtonElement>,
    action: () => void,
  ) => {
    const btn = e.currentTarget;
    btn.style.transition = "transform 140ms cubic-bezier(0.22, 1, 0.36, 1)";
    btn.style.transform = "scale(0.95)";
    window.setTimeout(() => {
      btn.style.transform = "scale(1)";
      action();
    }, 160);
  };

  const goServices = (e: React.MouseEvent<HTMLButtonElement>) => {
    pressAndGo(e, () => {
      navigate("/services");
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 0);
    });
  };

  const goContacts = (e: React.MouseEvent<HTMLButtonElement>) => {
    pressAndGo(e, () => {
      navigate("/contacts");
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 0);
    });
  };

  return (
    <section
      ref={sectionRef}
      id="invitation"
      className="relative w-full bg-background-50 text-foreground-950"
      style={{
        height: "200vh",
        contain: "layout paint",
        isolation: "isolate",
      }}
    >
      {/* Sticky one-screen stage — the final frame equals viewport height */}
      <div
        className="sticky top-0 w-full overflow-hidden"
        style={{ height: "100vh", contain: "layout paint" }}
      >
        <div className="relative w-full h-full flex flex-col items-center justify-center px-6 md:px-10 py-10 md:py-14 overflow-hidden">
          {/* Soft concentric guide rings */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="absolute rounded-full border border-foreground-950/8"
              style={{ width: "120vh", height: "120vh" }}
            ></div>
            <div
              className="absolute rounded-full border border-foreground-950/6"
              style={{ width: "80vh", height: "80vh" }}
            ></div>
          </div>

          {/* 1. Top small label */}
          <div ref={labelRef} className="relative z-20 w-full flex justify-center" style={{ opacity: 0 }}>
            <span
              className="uppercase font-medium text-foreground-950/55"
              style={{ fontSize: "12px", letterSpacing: "0.35em" }}
            >
              The Invitation
            </span>
          </div>

          {/* 2. Three-column main row */}
          <div
            className="relative z-20 w-full max-w-[1400px] mx-auto"
            style={{ marginTop: "36px" }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-6 md:gap-14">
              {/* Left column — right-aligned phrase */}
              <div
                ref={leftTextRef}
                className="w-full md:flex-1 md:text-right order-2 md:order-1"
                style={{ opacity: 0, transform: "translate3d(-80px, 0, 0)" }}
              >
                <p
                  className="text-foreground-950 select-none text-center md:text-right"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(3.25rem, 5.4vw, 5.25rem)",
                    fontWeight: 300,
                    lineHeight: 1.06,
                    letterSpacing: "-0.025em",
                    color: "#1a1330",
                  }}
                >
                  You&rsquo;ve walked
                  <br />
                  with us this far.
                </p>
              </div>

              {/* Center column — portrait image */}
              <div className="w-full md:w-auto md:flex-shrink-0 flex justify-center order-1 md:order-2">
                <div
                  ref={imageRef}
                  className="relative overflow-hidden"
                  style={{
                    width: "min(440px, 78vw)",
                    aspectRatio: "3 / 4",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    transformOrigin: "center center",
                    opacity: 0,
                    transform: "rotate(-720deg) scale(0.5)",
                  }}
                >
                  <img
                    src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/6f53e36662a9f374abfa6f86a2d7cb20.png"
                    alt="A silhouetted figure stepping through a doorway bathed in violet light — a threshold, an invitation."
                    title="Invitation · The Threshold"
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center" }}
                  />
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(255, 246, 234, 0.05) 0%, rgba(255, 246, 234, 0.0) 40%, rgba(255, 246, 234, 0.06) 100%)",
                    }}
                  ></div>
                  <div className="absolute right-3 bottom-3 text-background-50/80">
                    <span
                      className="uppercase font-medium"
                      style={{ fontSize: "10px", letterSpacing: "0.3em" }}
                    >
                      Fig. 05 / The Invitation
                    </span>
                  </div>
                </div>
              </div>

              {/* Right column — left-aligned italic phrase */}
              <div
                ref={rightTextRef}
                className="w-full md:flex-1 md:text-left order-3"
                style={{ opacity: 0, transform: "translate3d(80px, 0, 0)" }}
              >
                <p
                  className="text-foreground-950 select-none text-center md:text-left italic"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(3.25rem, 5.4vw, 5.25rem)",
                    fontWeight: 300,
                    lineHeight: 1.06,
                    letterSpacing: "-0.025em",
                    color: "#1a1330",
                  }}
                >
                  Where you go
                  <br />
                  is entirely yours.
                </p>
              </div>
            </div>
          </div>

          {/* 3. Buttons */}
          <div
            ref={buttonsRef}
            className="relative z-20 flex flex-col sm:flex-row items-center justify-center"
            style={{ marginTop: "36px", gap: "40px", opacity: 0, transform: "translate3d(0, 20px, 0)" }}
          >
            <button
              type="button"
              onClick={goServices}
              className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
            >
              <span
                className="uppercase font-medium"
                style={{ fontSize: "11px", letterSpacing: "0.3em" }}
              >
                See Services
              </span>
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                <i className="ri-arrow-right-up-line text-lg"></i>
              </span>
            </button>

            <button
              type="button"
              onClick={goContacts}
              className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
            >
              <span
                className="uppercase font-medium"
                style={{ fontSize: "11px", letterSpacing: "0.3em" }}
              >
                Contact Us
              </span>
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                <i className="ri-arrow-right-up-line text-lg"></i>
              </span>
            </button>
          </div>

          {/* 4. Footnote */}
          <div
            ref={footnoteRef}
            className="relative z-20"
            style={{ marginTop: "20px", opacity: 0, transform: "translate3d(0, 12px, 0)" }}
          >
            <p
              className="italic text-foreground-950/70 text-center"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "16px",
                fontWeight: 300,
                lineHeight: 1.6,
              }}
            >
              Or simply &mdash; take a breath. There&rsquo;s no rush.
            </p>
          </div>

          {/* Bottom corner meta */}
          <div
            ref={bottomLeftRef}
            className="absolute bottom-6 left-6 md:left-10 z-30 flex items-center gap-3 text-foreground-950/55"
            style={{ opacity: 0 }}
          >
            <span
              className="uppercase font-medium"
              style={{ fontSize: "10px", letterSpacing: "0.3em" }}
            >
              Chapter 05
            </span>
            <span className="w-8 h-[1px] bg-foreground-950/40"></span>
            <span
              className="uppercase font-medium"
              style={{ fontSize: "10px", letterSpacing: "0.3em" }}
            >
              Invitation
            </span>
          </div>

          <div
            ref={bottomRightRef}
            className="absolute bottom-6 right-6 md:right-10 z-30 flex items-center gap-3 text-foreground-950/55"
            style={{ opacity: 0 }}
          >
            <span
              className="uppercase font-medium"
              style={{ fontSize: "10px", letterSpacing: "0.3em" }}
            >
              Scroll Complete
            </span>
            <span className="w-8 h-[1px] bg-foreground-950/40"></span>
          </div>
        </div>
      </div>

      {/* Keep prefetch anchor for Services route */}
      <Link to="/services" className="hidden" aria-hidden="true" tabIndex={-1}>
        services
      </Link>
    </section>
  );
}